import { isAndroidMiniapp } from "@/utils/utilities";
import { create } from "zustand";

export type BottomBarStyle = "classic" | "floating";

interface SettingState {
  isFullscreen: boolean;
  bottomBarStyle: BottomBarStyle;
  sidebarCollapsed: boolean;
  setFullscreen: (value: boolean) => void;
  setBottomBarStyle: (value: BottomBarStyle) => void;
  toggleSidebar: () => void;
}

interface PersistedSettings {
  isFullscreen: boolean;
  bottomBarStyle: BottomBarStyle;
  sidebarCollapsed: boolean;
}

const getInitialSettings = (): PersistedSettings => {
  const fullscreenDefault = isAndroidMiniapp();
  const defaults: PersistedSettings = {
    isFullscreen: fullscreenDefault,
    bottomBarStyle: "floating",
    // Starts as an icon rail — the user opts into the wider pane.
    sidebarCollapsed: true,
  };

  if (typeof window === "undefined") return defaults;

  const savedSettings = localStorage.getItem("app-settings");
  if (savedSettings) {
    const parsed = JSON.parse(savedSettings);
    return {
      isFullscreen: parsed.isFullscreen ?? fullscreenDefault,
      bottomBarStyle: parsed.bottomBarStyle ?? "floating",
      sidebarCollapsed: parsed.sidebarCollapsed ?? defaults.sidebarCollapsed,
    };
  }

  return defaults;
};

const saveSettings = (settings: PersistedSettings) => {
  try {
    localStorage.setItem("app-settings", JSON.stringify(settings));
  } catch (error) {
    console.error("Error saving settings to localStorage:", error);
  }
};

const initialSettings = getInitialSettings();

export const useSettingStore = create<SettingState>((set, get) => {
  // Write the whole settings object from current state, so adding a setting
  // can't drop the ones a given setter doesn't know about.
  const persist = () => {
    const { isFullscreen, bottomBarStyle, sidebarCollapsed } = get();
    saveSettings({ isFullscreen, bottomBarStyle, sidebarCollapsed });
  };

  return {
    isFullscreen: initialSettings.isFullscreen,
    bottomBarStyle: initialSettings.bottomBarStyle,
    sidebarCollapsed: initialSettings.sidebarCollapsed,

    setFullscreen: (value) => {
      if (isAndroidMiniapp()) {
        set({ isFullscreen: value });
        persist();

        const WebApp = window.Eitaa!.WebApp!;
        if (value === true) {
          WebApp.requestFullscreen?.();
        } else {
          WebApp.exitFullscreen?.();
        }
      }
    },

    setBottomBarStyle: (value) => {
      set({ bottomBarStyle: value });
      persist();
    },

    toggleSidebar: () => {
      set({ sidebarCollapsed: !get().sidebarCollapsed });
      persist();
    },
  };
});
